import { getCurrencySymbol, NgTemplateOutlet, NgFor } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
  DialogService,
  DIALOG_RETURN_TYPES,
  FormDialogComponent,
} from '@next/core-lib/dialog';
import { Currency, LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { TableColumn, TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { skipWhile, take, withLatestFrom } from 'rxjs/operators';
import { getSelectedProductCountry } from 'src/app/product/store/selectors/product.selectors';
import { Product } from 'src/app/shared/models/shared.model';

import * as actions from '../../../product/store/actions/product.actions';
import { REGIONS } from '../../../shared/models/regions';
import { FeedAdvice, Phase } from '../../models/feed-advice.model';

export interface PriceSettingsDialogInputData extends AbstractFormDialogData {
  phases?: Phase[];
  products?: Product[];
  feedAdvice: FeedAdvice;
}

export interface PriceSettingsDialogResultData {
  phases?: Phase[];
  products?: Product[];
}

@Component({
  selector: 'app-price-settings',
  templateUrl: './price-settings.component.html',
  styleUrls: ['./price-settings.component.scss'],
  imports: [
    FormDialogComponent,
    ReactiveFormsModule,
    TableComponent,
    NgTemplateOutlet,
    MatSelect,
    NgFor,
    MatOption,
    TranslatePipe,
  ],
})
export class PriceSettingsComponent
  extends AbstractFormDialog<PriceSettingsDialogResultData>
  implements OnInit
{
  private readonly tableConfigBase: TableConfig = {
    dataSource: undefined,
    columns: undefined,
    selectColumn: false,
    actionColumn: false,
    pagination: false,
    disableCards: true,
    footer: false,
    scrolling: false,
    wrapperWidth: '100%',
  };
  currency: Currency = null;
  priceSettingsTableConfig: TableConfig;
  priceSettings = new FormArray([]);
  formGroup = new FormGroup({});
  priceSettingsColumns: TableColumn[] = [];

  regions: Array<{ id: string; value: string }> = REGIONS.sort((a, b) =>
    a.value < b.value ? -1 : 1,
  );

  selectedRegion$ = this.store.select(getSelectedProductCountry);
  location$ = this.store.select(UserSelectors.selectLocation);
  selectedRegionId: string;
  defaultSelectedRegionId: string;

  regionSelectionChanged: boolean;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PriceSettingsDialogInputData,
    protected mdDialogRef: MatDialogRef<
      PriceSettingsComponent,
      AbstractDialogResultData<PriceSettingsDialogResultData>
    >,
    private readonly locale: LocaleService,
    private readonly store: Store<RootState>,
    private readonly dialog: DialogService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.data.title = this.locale.translate('price-settings.title');
    this.data.altButton = true;
    this.data.altButtonText = this.locale.translate('price-settings.clear');
    this.data.altButtonColor = 'primary';
    this.data.altButtonIcon = 'delete';
    this.currency = this.locale.getCurrency();

    this.location$
      .pipe(
        withLatestFrom(this.selectedRegion$),
        skipWhile(([_, selectedCountry]) => selectedCountry === null),
        take(1),
      )
      .subscribe(
        ([location, selectedCountry]) =>
          (this.selectedRegionId = selectedCountry ? selectedCountry : location.country),
      );

    this.initColumns();
    this.initTable();
  }

  private initTable() {
    this.priceSettingsTableConfig = {
      ...this.tableConfigBase,
    };

    let datasource = null;
    if (this.data.phases) {
      datasource = this.data.phases?.map((p) => ({
        phaseNumber: p.phaseNumber,
        productName: p.product?.name,
        unitNettoPrice: p.product?.unitNettoPrice,
      }));
    } else {
      datasource = this.data.products?.map((p) => ({
        productName: p.name,
        unitNettoPrice: p.unitNettoPrice,
      }));
    }

    this.priceSettingsTableConfig.dataSource = new MatTableDataSource(datasource);
    this.priceSettingsTableConfig.columns = this.priceSettingsColumns;
  }

  private initColumns() {
    this.priceSettingsColumns = [
      {
        id: 'productName',
        type: TableColumnTypes.STRING,
        label: this.locale.translate('price-settings.columns.productName'),
      },
      {
        id: 'unitNettoPrice',
        type: TableColumnTypes.INPUT_NUMBER,
        label: `${this.locale.translate('price-settings.columns.unitNettoPrice')}
          (${getCurrencySymbol(this.currency, 'narrow')}/100kg)`,
        inputConfig: {
          formArray: this.priceSettings,
          step: 0.01,
        },
      },
    ];
    if (this.data.phases) {
      this.priceSettingsColumns.push({
        id: 'phaseNumber',
        type: TableColumnTypes.NUMBER,
        label: this.locale.translate('price-settings.columns.phaseNumber'),
      });
    }
  }

  changeRegionSelection(selection: MatSelectChange) {
    this.selectedRegionId = selection.value as string;
    this.regionSelectionChanged = true;
  }

  cancel() {
    if ((this.selectedRegionId && this.regionSelectionChanged) || this.priceSettings.dirty) {
      this.dialog
        .pendingChanges({
          title: this.locale.translate('create-feed-advice.pending-changes-warning.title'),
          subTitle: this.locale.translate('create-feed-advice.pending-changes-warning.message'),
          pushHistoryState: false,
        })
        .subscribe((result) => {
          if (result.status === DIALOG_RETURN_TYPES.confirm) {
            this.mdDialogRef.close();
          } else {
            this.canceling = false;
            this.mdDialogRef.disableClose = true;
          }
        });
    } else {
      this.mdDialogRef.close();
    }
  }

  alt() {
    this.priceSettings.controls.forEach((x) => x.setValue(null));
  }

  save() {
    const phases = this.data.phases?.map(
      (phase) =>
        <Phase>{
          ...phase,
          product: {
            ...phase.product,
            unitNettoPrice: <number>this.priceSettings.controls[phase.phaseNumber - 1].value,
            currency: this.locale.getCurrency(),
          },
        },
    );
    const products = this.data.products?.map(
      (product, index) =>
        <Product>{
          ...product,
          unitNettoPrice: <number>this.priceSettings.controls[index].value,
          currency: this.locale.getCurrency(),
        },
    );
    this.confirm({ phases, products });

    if (this.selectedRegionId && this.regionSelectionChanged) {
      this.store.dispatch(
        actions.setSelectedCountry({ save: true, id: this.selectedRegionId as string }),
      );
    }
  }
}
